import type { IBlogPostCard } from '@/interfaces';
import { getBlogPostPath } from '@/utils';
import Image, { type ImageLoader } from 'next/image';
import type React from 'react';
import {
  BlogCardBody,
  BlogCardDate,
  BlogCardExcerpt,
  BlogCardLink,
  BlogCardThumbnail,
  BlogCardTitle,
} from './Blog.styles';

export const formatBlogDate = (isoDate: string) => {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '';

  // The blog is English-only in v1, so the date format is fixed to en-US. timeZone is pinned
  // because a date-only value parses as UTC midnight: without it, a reader west of UTC would
  // hydrate a different day than the server rendered.
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
};

// The grid is 1/2/3 columns depending on breakpoint, so a card is at most the full viewport
// width on mobile and roughly a third of the 1160px container on desktop.
const THUMBNAIL_SIZES = '(min-width: 800px) 380px, (min-width: 600px) 50vw, 100vw';

// Resize at Contentful's CDN, not /_next/image: the pod-side optimizer has no sharp and 502s
// on the multi-MB originals these thumbnails are uploaded as.
const contentfulThumbnailLoader: ImageLoader = ({ src, width, quality }) =>
  `${src}${src.includes('?') ? '&' : '?'}w=${width}&fm=webp&q=${quality || 75}`;

const BlogCard: React.FC<{ post: IBlogPostCard }> = ({ post }) => (
  <li>
    <BlogCardLink href={getBlogPostPath(post.slug)}>
      {post.thumbnail?.url && (
        <BlogCardThumbnail>
          <Image
            loader={contentfulThumbnailLoader}
            src={post.thumbnail.url}
            alt={post.thumbnail.description || post.title}
            fill
            sizes={THUMBNAIL_SIZES}
          />
        </BlogCardThumbnail>
      )}
      <BlogCardBody>
        <BlogCardDate>{formatBlogDate(post.publishedDate)}</BlogCardDate>
        <BlogCardTitle>{post.title}</BlogCardTitle>
        {post.excerpt && <BlogCardExcerpt>{post.excerpt}</BlogCardExcerpt>}
      </BlogCardBody>
    </BlogCardLink>
  </li>
);

export default BlogCard;
